import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, 
  Play, 
  CheckCircle, 
  Clock, 
  BookOpen, 
  LogOut,
  Lock,
  Award,
  BarChart3,
  Target,
  Video,
  PlayCircle
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';

const ModuleView = ({ selectedModule, onNavigate, onBack }) => {
  const { logout } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [moduleStats, setModuleStats] = useState({
    totalChapters: 0,
    completedChapters: 0,
    totalDuration: 0,
    estimatedTime: ''
  });

  const [calculatedDurations, setCalculatedDurations] = useState(new Map());

  // Trigger animations after content loads
  useEffect(() => {
    if (!loading && chapters.length > 0) {
      setTimeout(() => setContentLoaded(true), 100);
    }
  }, [loading, chapters]);

  useEffect(() => {
    if (selectedModule) {
      // Scroll to top when component mounts
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      if (selectedModule.chapters && selectedModule.chapters.length > 0) {
        setChapters(selectedModule.chapters);
        calculateModuleStats(selectedModule.chapters);
        setLoading(false);
      } else {
        fetchChapters();
      }
    }
  }, [selectedModule]);

  const fetchChapters = async () => {
    try {
      setLoading(true);
      const chaptersData = await apiService.getChapters(selectedModule.id);
      setChapters(chaptersData);
      calculateModuleStats(chaptersData);
    } catch (error) {
      console.error('Failed to fetch chapters:', error);
      setError('Error al cargar los capítulos. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Priority-based duration calculation
  const calculateModuleStats = (chaptersData) => {
    const totalChapters = chaptersData.length;
    const completedChapters = chaptersData.filter(chapter => chapter.completed).length;
    
    // PRIORITY 1: Use pre-calculated total_duration from Dashboard if available
    if (selectedModule.total_duration && selectedModule.total_duration !== '0min') {
      console.log('✅ Using pre-calculated duration from Dashboard:', selectedModule.total_duration);
      
      setModuleStats({
        totalChapters,
        completedChapters,
        totalDuration: selectedModule.total_minutes || 0,
        estimatedTime: selectedModule.total_duration
      });
      return;
    }

    // PRIORITY 2: Calculate from individual chapters
    console.log('⚙️ Calculating duration from chapters...');
    const totalMinutes = chaptersData.reduce((total, chapter) => {
      let minutes = 0;
      
      // Check calculatedDurations map first (real-time calculated)
      if (calculatedDurations.has(chapter.id)) {
        const calculated = calculatedDurations.get(chapter.id);
        if (calculated.durationSeconds && !isNaN(calculated.durationSeconds)) {
          minutes = Math.floor(calculated.durationSeconds / 60);
          return total + minutes;
        }
      }
      
      // Then check chapter.calculatedDuration (from Dashboard)
      if (chapter.calculatedDuration) {
        const duration = chapter.calculatedDuration.toLowerCase();
        
        const hoursMatch = duration.match(/(\d+)h/);
        if (hoursMatch) {
          minutes += parseInt(hoursMatch[1]) * 60;
        }
        
        const minutesMatch = duration.match(/(\d+)\s*min/);
        if (minutesMatch) {
          minutes += parseInt(minutesMatch[1]);
        }
        
        const secondsMatch = duration.match(/(\d+)\s*seg/);
        if (secondsMatch && !minutesMatch) {
          minutes += Math.ceil(parseInt(secondsMatch[1]) / 60);
        }
        
        return total + minutes;
      }
      
      // Fallback to chapter.duration
      if (chapter.duration) {
        const duration = chapter.duration.toLowerCase();
        
        const hoursMatch = duration.match(/(\d+)h/);
        if (hoursMatch) {
          minutes += parseInt(hoursMatch[1]) * 60;
        }
        
        const minutesMatch = duration.match(/(\d+)\s*min/);
        if (minutesMatch) {
          minutes += parseInt(minutesMatch[1]);
        }
        
        const secondsMatch = duration.match(/(\d+)\s*seg/);
        if (secondsMatch && !minutesMatch) {
          minutes += Math.ceil(parseInt(secondsMatch[1]) / 60);
        }
        
        return total + minutes;
      }
      
      return total;
    }, 0);
    
    let estimatedTime;
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      estimatedTime = remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
    } else if (totalMinutes > 0) {
      estimatedTime = `${totalMinutes}min`;
    } else {
      estimatedTime = 'Calculando...';
    }

    console.log('📊 Calculated stats:', { totalMinutes, estimatedTime });

    setModuleStats({
      totalChapters,
      completedChapters,
      totalDuration: totalMinutes,
      estimatedTime
    });
  };

  const handleChapterClick = (chapter) => {
    onNavigate('chapter', { 
      selectedModule, 
      selectedChapter: chapter,
      chapters 
    });
  };

  const getChapterStatus = (chapter, index) => {
    if (chapter.completed) {
      return { status: 'completed', icon: CheckCircle, color: 'text-green-600 bg-green-100' };
    }
    
    return { status: 'available', icon: Play, color: 'text-blue-600 bg-blue-100' };
  };

  const isChapterAccessible = (chapter, index) => {
    return true;
  };

  const getProgressPercentage = () => {
    if (moduleStats.totalChapters === 0) return 0;
    return Math.round((moduleStats.completedChapters / moduleStats.totalChapters) * 100);
  };

  const VideoThumbnail = ({ chapter, isAccessible }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [thumbnailLoading, setThumbnailLoading] = useState(false);
    const [thumbnailError, setThumbnailError] = useState(false);
    const [videoDuration, setVideoDuration] = useState(null);
    const [durationLoading, setDurationLoading] = useState(false);
    const processingRef = useRef(false);
    const processedUrlRef = useRef(null);

    useEffect(() => {
      if (chapter.video_url && isAccessible && !processingRef.current && processedUrlRef.current !== chapter.video_url) {
        console.log(`🎬 Starting thumbnail for chapter ${chapter.id}: ${chapter.title}`);
        processingRef.current = true;
        processedUrlRef.current = chapter.video_url;
        generateThumbnailAndDuration();
      }
    }, [chapter.video_url, isAccessible]);

    const formatDuration = (seconds) => {
      if (!seconds || isNaN(seconds)) return null;
      
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      
      if (minutes > 0) {
        return remainingSeconds > 0 ? `${minutes}min ${remainingSeconds}seg` : `${minutes}min`;
      }
      return `${remainingSeconds}seg`;
    };

    const generateThumbnailAndDuration = async () => {
      if (!chapter.video_url) {
        console.warn(`⚠️ No video_url for chapter ${chapter.id}`);
        return;
      }
      
      console.log(`📹 Chapter ${chapter.id} video_url:`, chapter.video_url);
      
      setThumbnailLoading(true);
      setDurationLoading(true);
      setThumbnailError(false);

      try {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        
        if (!video || !canvas) {
          console.error(`❌ Missing video or canvas element for chapter ${chapter.id}`);
          processingRef.current = false;
          return;
        }

        const videoUrl = apiService.getVideoUrl(chapter.video_url);
        console.log(`🔗 Constructed video URL for chapter ${chapter.id}:`, videoUrl);
        
        // CRITICAL FIX: Set crossOrigin BEFORE src
        // This is essential for Backblaze B2 and CORS
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.muted = true;
        
        // Set src AFTER crossOrigin
        video.src = videoUrl;
        
        console.log(`✅ Video attributes set for chapter ${chapter.id}`);
        
        video.onloadedmetadata = () => {
          console.log(`✅ Video metadata loaded for chapter ${chapter.id}`);
          try {
            const duration = video.duration;
            console.log(`⏱️ Video duration for chapter ${chapter.id}:`, duration);
            
            if (duration && !isNaN(duration)) {
              const formattedDuration = formatDuration(duration);
              setVideoDuration(formattedDuration);
              console.log(`📊 Formatted duration for chapter ${chapter.id}:`, formattedDuration);
              
              // Update parent's calculatedDurations
              if (formattedDuration) {
                setCalculatedDurations(prev => {
                  const newMap = new Map(prev);
                  newMap.set(chapter.id, {
                    formattedDuration,
                    durationSeconds: duration
                  });
                  return newMap;
                });
              }
            }
            setDurationLoading(false);

            // Seek to 10% or 10 seconds, whichever is smaller
            const seekTime = Math.min(duration * 0.1, 10);
            console.log(`⏭️ Seeking to ${seekTime}s for chapter ${chapter.id}`);
            video.currentTime = seekTime;
          } catch (error) {
            console.error(`❌ Error processing video metadata for chapter ${chapter.id}:`, error);
            setDurationLoading(false);
            video.currentTime = 0;
          }
        };

        // Use onseeked event (same as Admin Panel)
        video.onseeked = () => {
          console.log(`✅ Video seeked for chapter ${chapter.id}, generating thumbnail...`);
          try {
            const ctx = canvas.getContext('2d');
            
            // Calculate canvas size based on video aspect ratio
            const aspectRatio = video.videoWidth / video.videoHeight;
            canvas.width = 320;
            canvas.height = Math.round(320 / aspectRatio);
            
            console.log(`🎨 Canvas dimensions for chapter ${chapter.id}: ${canvas.width}x${canvas.height}`);
            
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            console.log(`✅ Thumbnail generated for chapter ${chapter.id}, size: ${thumbnailUrl.length} bytes`);
            
            setThumbnail(thumbnailUrl);
            setThumbnailLoading(false);
            processingRef.current = false;
          } catch (error) {
            console.error(`❌ Error generating thumbnail for chapter ${chapter.id}:`, error);
            console.error('Error details:', {
              name: error.name,
              message: error.message,
              videoWidth: video.videoWidth,
              videoHeight: video.videoHeight
            });
            setThumbnailError(true);
            setThumbnailLoading(false);
            processingRef.current = false;
          }
        };

        video.onerror = (e) => {
          console.error(`❌ Video error for chapter ${chapter.id}:`, {
            error: e,
            errorCode: video.error?.code,
            errorMessage: video.error?.message,
            videoUrl: videoUrl,
            crossOrigin: video.crossOrigin,
            networkState: video.networkState,
            readyState: video.readyState
          });
          
          // Provide specific error message
          let errorMsg = 'Error loading video for thumbnail';
          if (video.error) {
            switch(video.error.code) {
              case 1: errorMsg = 'Video loading aborted'; break;
              case 2: errorMsg = 'Network error loading video'; break;
              case 3: errorMsg = 'Video decoding failed'; break;
              case 4: errorMsg = 'Video format not supported or CORS error'; break;
            }
          }
          console.error(`   Error type: ${errorMsg}`);
          
          setThumbnailError(true);
          setThumbnailLoading(false);
          setDurationLoading(false);
          processingRef.current = false;
        };

        console.log(`🎬 Loading video for chapter ${chapter.id}...`);

      } catch (error) {
        console.error(`❌ Error setting up video processing for chapter ${chapter.id}:`, error);
        setThumbnailError(true);
        setThumbnailLoading(false);
        setDurationLoading(false);
        processingRef.current = false;
      }
    };

    if (!chapter.video_url) {
      return (
        <div className="w-full h-36 sm:h-40 md:h-42 bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="w-full h-full flex items-center justify-center bg-gray-200">
            <Video className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-gray-400" />
          </div>
        </div>
      );
    }

    if (!isAccessible) {
      return (
        <div className="w-full h-36 sm:h-40 md:h-42 bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <Lock className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Bloqueado</p>
          </div>
        </div>
      );
    }

    return (
      <div className="relative w-full h-36 sm:h-40 md:h-42 bg-gray-100 rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          style={{ display: 'none' }}
          muted
          preload="metadata"
        />
        <canvas
          ref={canvasRef}
          style={{ display: 'none' }}
        />

       

        {thumbnail ? (
          <>
            <img
              src={thumbnail}
              alt={`Vista previa de ${chapter.title}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
              <PlayCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs">
              {durationLoading ? (
                <div className="flex items-center space-x-1">
                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                  <span>...</span>
                </div>
              ) : (
                videoDuration || chapter.calculatedDuration || chapter.duration || 'N/A'
              )}
            </div>
          </>
        ) : (
          !thumbnailLoading && !thumbnailError && (
            <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
              <Video className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-purple-600" />
            </div>
          )
        )}
      </div>
    );
  };

  const ChapterCard = ({ chapter, index }) => {
    const { status, icon: StatusIcon, color } = getChapterStatus(chapter, index);
    const isAccessible = isChapterAccessible(chapter, index);

    return (
      <div
        className={`bg-white rounded-lg shadow-sm border overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-lg transform hover:scale-[1.02] ${
          chapter.completed ? 'bg-green-50 border-green-200' : ''
        }`}
        onClick={() => handleChapterClick(chapter)}
      >
        <div className="p-3 sm:p-4 pb-0">
          <VideoThumbnail chapter={chapter} isAccessible={isAccessible} />
        </div>

        <div className="p-3 sm:p-4">
          <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <div className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
                {chapter.completed ? (
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <span className="text-xs sm:text-sm font-bold">{index + 1}</span>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 line-clamp-2">
                  {chapter.title}
                </h3>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center space-x-3 sm:space-x-4 text-xs sm:text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">
                  {calculatedDurations.has(chapter.id) 
                    ? calculatedDurations.get(chapter.id).formattedDuration 
                    : chapter.calculatedDuration || chapter.duration || 'Calculando...'
                  }
                </span>
              </div>
              {chapter.video_url && (
                <div className="flex items-center space-x-1">
                  <Video className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Video</span>
                </div>
              )}
            </div>
            
            <div className="flex-shrink-0">
              {chapter.completed && (
                <span className="px-2 py-0.5 sm:py-1 bg-green-100 text-green-800 text-xs rounded-full whitespace-nowrap">
                  Completado
                </span>
              )}
              {!chapter.completed && (
                <span className="px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-xs rounded-full whitespace-nowrap">
                  Disponible
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Recalculate stats when calculatedDurations change (from video thumbnails)
  useEffect(() => {
    if (chapters.length > 0 && calculatedDurations.size > 0) {
      // Only recalculate if we don't already have a pre-calculated duration from Dashboard
      if (!selectedModule.total_duration || selectedModule.total_duration === '0min') {
        console.log('🔄 Recalculating stats with new video durations...');
        calculateModuleStats(chapters);
      }
    }
  }, [calculatedDurations]);

  // Initial stats calculation
  useEffect(() => {
    if (chapters.length > 0) {
      calculateModuleStats(chapters);
    }
  }, [chapters.length]);

  // Animation styles
  const animationStyles = `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
    .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
    .animate-scaleIn { animation: scaleIn 0.4s ease-out forwards; }
    .stagger-1 { animation-delay: 0.1s; }
    .stagger-2 { animation-delay: 0.2s; }
    .stagger-3 { animation-delay: 0.3s; }
    .stagger-4 { animation-delay: 0.4s; }
  `;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">Cargando capítulos...</p>
        </div>
      </div>
    );
  }

  if (!selectedModule) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4 text-sm sm:text-base">No se encontró el módulo seleccionado</p>
          <button
            onClick={onBack}
            className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{animationStyles}</style>
      
      {/* Header */}
      <header className={`bg-white shadow-sm border-b ${contentLoaded ? 'animate-fadeIn' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center gap-2">
          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 min-w-0 flex-1">
            <button
              onClick={onBack}
              className="p-1.5 sm:p-2 text-gray-600 hover:text-blue-900 transition-all duration-300 rounded-lg hover:bg-gray-100 transform hover:scale-105 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
              <span className="text-xl sm:text-2xl flex-shrink-0">{selectedModule.icon}</span>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-blue-900 truncate">{selectedModule.title}</h1>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-1">{selectedModule.description}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Module Overview */}
        <div className={`bg-white rounded-lg shadow-sm border p-3 sm:p-4 mb-4 sm:mb-6 ${contentLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="flex items-center space-x-2">
              <div className="bg-blue-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600">Capítulos</p>
                <p className="text-base sm:text-lg font-bold text-gray-900">{moduleStats.totalChapters}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="bg-green-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600">Completados</p>
                <p className="text-base sm:text-lg font-bold text-gray-900">{moduleStats.completedChapters}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="bg-purple-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600">Duración</p>
                <p className="text-base sm:text-lg font-bold text-gray-900 truncate">{moduleStats.estimatedTime}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="bg-orange-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-600">Progreso</p>
                <p className="text-base sm:text-lg font-bold text-gray-900">{getProgressPercentage()}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className={`bg-white rounded-lg shadow-sm border p-3 sm:p-4 mb-4 sm:mb-6 ${contentLoaded ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}>
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Progreso</h2>
            <span className="text-base sm:text-lg font-bold text-blue-900">{getProgressPercentage()}%</span>
          </div>
          
          <div className="w-full h-2 sm:h-2.5 md:h-3 bg-gray-200 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          
          <div className="flex justify-between text-xs sm:text-sm text-gray-600 mt-2">
            <span>{moduleStats.completedChapters} completados</span>
            <span>{moduleStats.totalChapters - moduleStats.completedChapters} restantes</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 animate-fadeIn">
            <p className="text-red-800 text-sm sm:text-base">{error}</p>
          </div>
        )}

        {/* Learning Path Instructions */}
        <div className={`bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 ${contentLoaded ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}>
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="bg-blue-100 p-1.5 rounded-lg flex-shrink-0">
              <Target className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-1">Acceso Libre</h3>
              <p className="text-xs sm:text-sm text-blue-800 mb-2">
                Todos los capítulos están disponibles. Aprende en el orden que prefieras.
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <span className="text-blue-800">Completado</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Play className="w-3 h-3 text-blue-600 flex-shrink-0" />
                  <span className="text-blue-800">Disponible</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chapters Grid */}
        <div className={`mb-4 sm:mb-6 ${contentLoaded ? 'animate-fadeInUp stagger-3' : 'opacity-0'}`}>
          <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Capítulos</h2>
            <div className="text-xs sm:text-sm text-gray-600">
              {moduleStats.completedChapters} de {moduleStats.totalChapters}
            </div>
          </div>
          
          <div className="grid gap-4 sm:gap-5 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {chapters.map((chapter, index) => (
              <ChapterCard key={chapter.id} chapter={chapter} index={index} />
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className={`bg-white rounded-lg shadow-sm border p-3 sm:p-4 ${contentLoaded ? 'animate-fadeInUp stagger-4' : 'opacity-0'}`}>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Acciones Rápidas</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <button
              onClick={() => {
                const nextChapter = chapters.find(chapter => !chapter.completed);
                if (nextChapter) handleChapterClick(nextChapter);
              }}
              disabled={moduleStats.completedChapters === moduleStats.totalChapters}
              className="flex items-center justify-center space-x-2 p-2.5 sm:p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
            >
              <Play className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm font-medium text-blue-900">Continuar</span>
            </button>
            
            <button
              onClick={() => {
                const firstChapter = chapters[0];
                if (firstChapter) handleChapterClick(firstChapter);
              }}
              disabled={chapters.length === 0}
              className="flex items-center justify-center space-x-2 p-2.5 sm:p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
            >
              <BookOpen className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm font-medium text-green-900">Comenzar</span>
            </button>
            
            <button
              onClick={() => {
                const lastCompleted = chapters.filter(chapter => chapter.completed).pop();
                if (lastCompleted) handleChapterClick(lastCompleted);
              }}
              disabled={moduleStats.completedChapters === 0}
              className="flex items-center justify-center space-x-2 p-2.5 sm:p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
            >
              <Award className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <span className="text-sm font-medium text-purple-900">Revisar</span>
            </button>
          </div>
        </div>

        {/* Module Completion Celebration */}
        {getProgressPercentage() === 100 && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 sm:p-5 md:p-6 mt-4 sm:mt-6 animate-scaleIn">
            <div className="text-center">
              <div className="bg-green-100 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-3 animate-bounce">
                <Award className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-green-600" />
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-green-900 mb-2">
                ¡Felicitaciones!
              </h3>
              <p className="text-green-800 mb-2 text-sm sm:text-base">
                Has completado <strong>{selectedModule.title}</strong>
              </p>
              <p className="text-xs sm:text-sm text-green-700">
                Continúa con el siguiente módulo.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ModuleView;